
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { getSidebarItem } from '../api';
import PageLayout from '../components/PageLayout';
import PageHeader from '../components/ui/PageHeader';
import PageMain from '../components/ui/PageMain';

function SidebarPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [itemName, setItemName] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadSidebarItem() {
      try {
        setIsLoading(true);
        setError('');
        setItemName('');

        const data = await getSidebarItem(id);

        if (cancelled) return;

        const name = data?.title ?? data?.name ?? data?.label ?? '';
        setItemName(name);

        const tagName = data?.tags?.[0];

        // No tag: stay on the page without displaying an error message.
        if (!tagName) return;

        navigate(`/tag/${encodeURIComponent(tagName)}`, {
          replace: true,
          state: { sidebarItemName: name },
        });
      } catch (err) {
        if (cancelled) return;

        console.error('Failed to load sidebar item:', err);
        setError('loadFailed');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadSidebarItem();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  return (
    <PageLayout showSearch showAiSearch>
      <PageMain>
        <PageHeader
          title={itemName || t('sidebarPage.title')}
          condensed
          note={
            !isLoading && !error
              ? t('common.emptyNote')
              : null
          }
        />

        {isLoading ? (
          <p className="page-message">{t('sidebarPage.loading')}</p>
        ) : error ? (
          <p className="page-message" role="alert">
            {t('sidebarPage.loadFailed')}
          </p>
        ) : null}
      </PageMain>
    </PageLayout>
  );
}

export default SidebarPage;
